import type { Theme, TokenTree } from './types';

/**
 * Walks a token tree and produces a flat map of CSS custom properties.
 * `color.primary.500` → `--color-primary-500`.
 *
 * The `name` and `id` keys at the root are theme metadata, not tokens, and
 * are skipped.
 */
export function flattenTokens(theme: Theme): Record<string, string> {
  const out: Record<string, string> = {};

  const walk = (node: string | TokenTree, path: string[]): void => {
    if (typeof node === 'string') {
      out[`--${path.join('-')}`] = node;
      return;
    }
    for (const [key, value] of Object.entries(node)) {
      walk(value, [...path, key]);
    }
  };

  for (const [key, value] of Object.entries(theme)) {
    if (key === 'name' || key === 'id') continue;
    if (typeof value === 'string') {
      out[`--${key}`] = value;
    } else {
      walk(value, [key]);
    }
  }
  return out;
}

/**
 * Applies a flat map of CSS variables to a target element. Defaults to the
 * document root, which is what every theme switch needs in this app.
 */
export function applyTokens(
  vars: Record<string, string>,
  target: HTMLElement = document.documentElement
): void {
  for (const [name, value] of Object.entries(vars)) {
    target.style.setProperty(name, value);
  }
}
